import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    Typography,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const AVAILABLE_COLUMNS = [
    { id: "name", label: "Org Name", defaultSelected: true },
    { id: "owners", label: "Org Owners", defaultSelected: true },
    { id: "url", label: "Org URL", defaultSelected: true },
    { id: "service_owner", label: "Service Owner", defaultSelected: true },
    { id: "approvers", label: "Approvers", defaultSelected: true },
    { id: "countries", label: "Countries", defaultSelected: true },
    { id: "copilot_link", label: "Copilot Requests Info", defaultSelected: true },
    { id: "org_website", label: "Org Website", defaultSelected: true },
];

const ExportModal = ({ open, onClose, onExport, totalRecords }) => {
    const [selectedColumns, setSelectedColumns] = useState(
        AVAILABLE_COLUMNS.filter(col => col.defaultSelected).map(col => col.id)
    );

    // Reset to all selected when modal opens
    useEffect(() => {
        if (open) {
            setSelectedColumns(AVAILABLE_COLUMNS.map(col => col.id));
        }
    }, [open]);

    const handleToggleColumn = (columnId) => {
        setSelectedColumns(prev => {
            if (prev.includes(columnId)) {
                // Don't allow deselecting all columns
                if (prev.length === 1) return prev;
                return prev.filter(id => id !== columnId);
            } else {
                return [...prev, columnId];
            }
        });
    };

    const handleSelectAll = () => {
        setSelectedColumns(AVAILABLE_COLUMNS.map(col => col.id));
    };

    const handleDeselectAll = () => {
        // Keep at least one column selected (first one)
        setSelectedColumns([AVAILABLE_COLUMNS[0].id]);
    };

    const handleExport = () => {
        onExport(selectedColumns);
        onClose();
    };

    const allSelected = selectedColumns.length === AVAILABLE_COLUMNS.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 2,
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FileDownloadOutlinedIcon sx={{ color: "#0b756c", fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
                        Export Organizations to CSV
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: "#666",
                        "&:hover": { backgroundColor: "#f5f5f5" }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                        Select the columns you want to include in the CSV export.
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#f8f9fa",
                            padding: "10px 16px",
                            borderRadius: "8px",
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
                            Exporting {totalRecords} organization{totalRecords !== 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                size="small"
                                onClick={handleSelectAll}
                                disabled={allSelected}
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.8rem",
                                    color: allSelected ? "#999" : "#1565c0",
                                }}
                            >
                                Select All
                            </Button>
                            <Divider orientation="vertical" flexItem />
                            <Button
                                size="small"
                                onClick={handleDeselectAll}
                                disabled={selectedColumns.length === 1}
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.8rem",
                                    color: selectedColumns.length === 1 ? "#999" : "#1565c0",
                                }}
                            >
                                Deselect All
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: "#333",
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        letterSpacing: "0.5px"
                    }}
                >
                    Columns ({selectedColumns.length} selected)
                </Typography>

                <FormGroup>
                    {AVAILABLE_COLUMNS.map((column) => (
                        <FormControlLabel
                            key={column.id}
                            control={
                                <Checkbox
                                    checked={selectedColumns.includes(column.id)}
                                    onChange={() => handleToggleColumn(column.id)}
                                    sx={{
                                        color: "#0b756c",
                                        "&.Mui-checked": {
                                            color: "#0b756c",
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontSize: "0.95rem" }}>
                                    {column.label}
                                </Typography>
                            }
                            sx={{
                                mb: 0.5,
                                padding: "4px 8px",
                                borderRadius: "6px",
                                "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                },
                            }}
                        />
                    ))}
                </FormGroup>

                {selectedColumns.length === 0 && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#d32f2f",
                            display: "block",
                            mt: 1,
                            fontSize: "0.85rem"
                        }}
                    >
                        Please select at least one column to export
                    </Typography>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 3,
                    pt: 2,
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        textTransform: "none",
                        color: "#666",
                        fontWeight: 500,
                        "&:hover": {
                            backgroundColor: "#f5f5f5",
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleExport}
                    variant="contained"
                    disabled={selectedColumns.length === 0}
                    startIcon={<FileDownloadOutlinedIcon />}
                    sx={{
                        textTransform: "none",
                        backgroundColor: "#0b756c",
                        fontWeight: 600,
                        px: 3,
                        "&:hover": {
                            backgroundColor: "#085a50",
                        },
                        "&:disabled": {
                            backgroundColor: "#ccc",
                            color: "#888",
                        },
                    }}
                >
                    Export CSV
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportModal;
